import React, { useState } from 'react';
import BaseModal from '../base/BaseModal';
import { Clock, CheckCircle2, XCircle, FileText } from 'lucide-react';
import { ExtensionService } from '../../services/extensionService';
import { showToast } from '../../utils/toastNotification';
import dayjs from 'dayjs';

export default function ExtensionApproveModal({
  isOpen,
  onClose,
  extension,
  onSuccess
}) {
  const [leadershipNote, setLeadershipNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !extension) return null;

  const complaintCode = extension.ma_phan_anh || extension.complaintCode || '';
  const complaintTitle = extension.tieu_de_phan_anh || extension.complaintTitle || 'Phản ánh kiến nghị';
  const category = extension.linh_vuc?.ten || extension.category || 'N/A';
  const neighborhood = extension.khu_pho || extension.neighborhood || 'N/A';
  const requestedBy = extension.nguoi_de_nghi?.ho_va_ten || extension.nguoi_de_nghi?.ten_dang_nhap || extension.requestedBy || 'N/A';
  const oldDeadline = extension.han_ban_dau || extension.oldDeadline;
  const requestedDeadline = extension.han_de_xuat_moi || extension.requestedDeadline;
  const reason = extension.ly_do_gia_han || extension.reason || '';
  const status = extension.trang_thai || extension.status || 'PENDING';
  const isPending = status === 'PENDING' || status === 'Chờ duyệt';
  const isApproved = status === 'APPROVED' || status === 'Đã duyệt';
  const approvedBy = extension.nguoi_duyet?.ho_va_ten || extension.nguoi_duyet?.ten_dang_nhap || extension.reviewedBy || 'Lãnh đạo UBND';
  const leadershipNoteText = extension.ly_do_tu_choi || extension.reviewNote || '';

  const oldDate = dayjs(oldDeadline);
  const newDate = dayjs(requestedDeadline);
  const daysDiff = newDate.diff(oldDate, 'day');
  const hoursDiff = newDate.diff(oldDate, 'hour') % 24;

  let diffText = `+${daysDiff} ngày`;
  if (hoursDiff > 0) {
    diffText += ` ${hoursDiff} giờ`;
  }

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await ExtensionService.approve(extension.id, leadershipNote);
      showToast.success('Phê duyệt gia hạn thời gian giải quyết thành công');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast.error(err.message || 'Phê duyệt thất bại');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!leadershipNote.trim()) {
      setError('Vui lòng nhập lý do từ chối gia hạn');
      return;
    }

    setIsSubmitting(true);
    try {
      await ExtensionService.reject(extension.id, leadershipNote.trim());
      showToast.success('Đã từ chối đề nghị gia hạn');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast.error(err.message || 'Từ chối thất bại');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-between w-full">
      <button
        type="button"
        onClick={onClose}
        disabled={isSubmitting}
        className="px-4 py-2 rounded-xl text-xs md:text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
      >
        Đóng
      </button>

      {isPending ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleReject}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-xl text-xs md:text-sm font-semibold text-white bg-red-600 hover:bg-red-700 shadow-sm transition-colors cursor-pointer flex items-center gap-1.5"
          >
            <XCircle className="w-4 h-4" />
            <span>Từ chối</span>
          </button>
          <button
            type="button"
            onClick={handleApprove}
            disabled={isSubmitting}
            className="px-5 py-2 rounded-xl text-xs md:text-sm font-bold text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors cursor-pointer flex items-center gap-1.5"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>Phê duyệt gia hạn</span>
          </button>
        </div>
      ) : (
        <div className="text-xs text-gray-500 italic">
          Đề nghị này đã được xử lý ({isApproved ? 'Đã phê duyệt' : 'Đã từ chối'}).
        </div>
      )}
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Thẩm định Yêu cầu Gia hạn Phản ánh ${complaintCode}`}
      subtitle="Xem xét và quyết định gia hạn thời gian giải quyết phản ánh kiến nghị"
      size="2xl"
      footer={footer}
    >
      <div className="space-y-4 py-1">
        {/* Block 1: Thông tin Phản ánh */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Thông tin phản ánh</span>
            <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${
              extension.muc_do === 'KHAN_CAP' || extension.muc_do === 'Khẩn cấp' || extension.urgency === 'Khẩn cấp' ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-blue-100 text-blue-700 border border-blue-200'
            }`}>
              {extension.muc_do === 'KHAN_CAP' || extension.muc_do === 'Khẩn cấp' || extension.urgency === 'Khẩn cấp' ? 'Khẩn cấp' : 'Thông thường'}
            </span>
          </div>

          <h4 className="text-sm md:text-base font-bold text-slate-900 leading-snug">
            {complaintTitle}
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1 text-xs text-slate-600">
            <div>
              <span className="text-slate-400 block">Lĩnh vực:</span>
              <span className="font-semibold text-slate-800">{category}</span>
            </div>
            <div>
              <span className="text-slate-400 block">Khu phố:</span>
              <span className="font-semibold text-slate-800">{neighborhood}</span>
            </div>
            <div>
              <span className="text-slate-400 block">Cán bộ đề nghị:</span>
              <span className="font-semibold text-slate-800">{requestedBy}</span>
            </div>
          </div>
        </div>

        {/* Block 2: So sánh Thời hạn (Diff View Box) */}
        <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-3.5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-900 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-blue-600" />
              So sánh điều chỉnh thời hạn SLA
            </span>
            <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-bold rounded-md">
              {diffText}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-white border border-rose-200 rounded-md p-2.5">
              <span className="text-xs text-rose-500 font-semibold block mb-1">Hạn ban đầu</span>
              <div className="text-sm font-bold text-rose-700 line-through">
                {dayjs(oldDeadline).format('DD/MM/YYYY HH:mm')}
              </div>
            </div>

            <div className="bg-white border border-emerald-300 rounded-md p-2.5">
              <span className="text-xs text-emerald-600 font-semibold block mb-1">Hạn đề xuất mới</span>
              <div className="text-sm font-bold text-emerald-700">
                {dayjs(requestedDeadline).format('DD/MM/YYYY HH:mm')}
              </div>
            </div>
          </div>
        </div>

        {/* Block 3: Lý do xin gia hạn */}
        <div className="space-y-1.5">
          <label className="text-xs md:text-sm font-semibold text-gray-700 block">
            Lý do xin gia hạn từ Cán bộ xử lý:
          </label>
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-xs md:text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
            {reason}
          </div>

          {/* Attachments */}
          {((extension.files && extension.files.length > 0) || (extension.attachments && extension.attachments.length > 0)) && (
            <div className="mt-2 space-y-1">
              <span className="text-xs text-gray-500 font-medium">Tài liệu đính kèm:</span>
              <div className="flex flex-wrap gap-2">
                {(extension.files || extension.attachments || []).map((att, idx) => (
                  <span
                    key={att.id || idx}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 rounded-md text-xs font-medium text-gray-700"
                  >
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                    <span className="max-w-[200px] truncate">{att.ten_file || att.name}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Block 4: Nhập phản hồi / Ghi chú của Lãnh đạo */}
        {isPending ? (
          <div>
            <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5">
              Ý kiến chỉ đạo / Ghi chú của Lãnh đạo <span className="text-xs text-gray-400 font-normal">(Bắt buộc khi Từ chối)</span>
            </label>
            <textarea
              rows={3}
              value={leadershipNote}
              onChange={(e) => {
                setLeadershipNote(e.target.value);
                if (error) setError('');
              }}
              placeholder="Nhập ý kiến chỉ đạo hoặc lý do từ chối nếu không chấp thuận..."
              className={`w-full p-3 bg-white border rounded-xl text-xs md:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none ${
                error ? 'border-red-500' : 'border-gray-200'
              }`}
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
        ) : (
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700">
            <span className="font-semibold block mb-0.5">Phản hồi của Lãnh đạo ({approvedBy}):</span>
            <p className="m-0 italic text-slate-600">{leadershipNoteText || 'Không có ghi chú thêm'}</p>
          </div>
        )}
      </div>
    </BaseModal>
  );
}
